import { useEffect } from 'react';
import { useReduxState } from 'hooks';
import { Link } from 'components/common';
import { Icon } from 'components/common/icons';

import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  dismissAfter: number;
  id: number;
  link?: { href: string; text: string };
  onDismiss: (id: number) => void;
  text: string;
  type: 'success' | 'warning' | 'error';
}

const Notice = ({ dismissAfter, id, link, onDismiss, text, type }: Props) => {
  const { theme } = useReduxState();

  useEffect(() => {
    if (dismissAfter === 0) return;
    const timeout = setTimeout(() => onDismiss(id), dismissAfter);

    return () => {
      clearTimeout(timeout);
    };
  }, [dismissAfter, id, onDismiss]);

  let backgroundColor = 'white';
  let color = theme.textColorLighter;
  let icon = 'info-circle' as IconProp;

  if (type === 'success') {
    backgroundColor = theme.successColor;
    color = theme.successTextColor;
    icon = 'check-circle';
  } else if (type === 'warning') {
    backgroundColor = theme.warningColor;
    color = theme.warningTextColor;
    icon = 'exclamation-triangle';
  } else if (type === 'error') {
    backgroundColor = theme.dangerColor;
    color = theme.dangerTextColor;
    icon = 'exclamation-circle';
  }

  return (
    <div className="notice">
      <Icon icon={icon} style={{ position: 'absolute', top: 18, left: 20 }} />
      <div className="notice-text" dangerouslySetInnerHTML={{ __html: text }} />
      {link ? (
        <div className="notice-link">
          <Link
            href={link.href}
            onClick={() => onDismiss(id)}
            style={{ color, textDecoration: 'underline' }}
            text={link.text}
          />
        </div>
      ) : null}
      <button className="close" onClick={() => onDismiss(id)}>
        <Icon icon="times" />
      </button>

      <style jsx>{`
        @keyframes fadein {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .notice {
          animation: fadein 1s;
          background-color: ${backgroundColor};
          box-shadow: ${theme.dropdownShadow};
          border-radius: ${theme.cardBorderRadius};
          color: ${color};
          font-size: 1.5rem;
          font-weight: 600;
          padding: 16px 60px 16px 45px;
          position: relative;
          width: 100%;
        }
        .notice:not(:first-child) {
          margin-top: 10px;
        }
        .notice-link {
          margin-top: 5px;
        }
        .close {
          background-color: transparent;
          border: 0;
          color: inherit;
          cursor: pointer;
          font-size: 1.6rem;
          height: 50px;
          padding: 0;
          position: absolute;
          right: 0;
          top: 0;
          text-align: center;
          width: 50px;
        }
      `}</style>
    </div>
  );
};

export default Notice;
